import Pageview from './Pageview.js';
import UserEvent from './UserEvent.js';

/**
 */
class Analytics {
  constructor({
    apiKey,
    integrations = { All: false, Intercom: true },
  }) {
    let deferred = {};
    const ready = new Promise((resolve, reject) => {
      deferred = { resolve, reject };
      window.onSegmentLoad = deferred.resolve;
      window.onSegmentError = deferred.reject;
      load(apiKey);
    });

    this.ready = function() {
      return ready;
    };

    const methods = ['event', 'page', 'user'];
    methods.forEach((method) => {
      this[method] = defer(this, this[method], ready);
    });

    this.shouldTrackEvents = true;
    this.shouldTrackPageviews = true;
    this.shouldTrackIdentity = true;
    this.integrations = integrations;

    this.hasIdentifiedUser = false;
    this._user = null;
  }

  /**
   * Clear out current user associations
   */
  reset() {
    window.analytics.reset();
    this.hasIdentifiedUser = false;
    this._user = null;
  }

  /**
   * Provide details about user & optionally their access
   * @param {session-manager.ApplicationUser} user
   * @param {session-manager.RoleUser} [roleUser]
   */
  user(user, roleUser) {
    if (this.shouldTrackIdentity) {
      const accountIds = user?.accountMemberships?.map((membership) => `${membership.account.id}`) || [];
      const details = {
        email: user.emailAddress,
        first_name: user.firstName,
        last_name: user.lastName,
        created_at: user.joinedAt,
        account_id: accountIds,
        // Follow pattern from Chart of using camelCase keys
        isStaff: user.isSuperuser || user.isStaff,
      };
      this._user = {
        id: user.id,
        email: details.email,
        first_name: details.first_name,
        last_name: details.last_name,
      };
      const permissions = {};
      // Follow pattern from Chart of using camelCase keys
      if (roleUser) {
        permissions.permissionsRole = roleUser.role;
        permissions.permissionsCreatedAt = roleUser.createdAt;
        permissions.permissionsExpiredAt = roleUser.expiredAt;
      }
      window.analytics.identify(
        user.id,
        { ...details, ...permissions }
      );

      // Connect user to relevant groups for analytics
      if (user.accountMemberships && user.accountMemberships.length > 0) {
        user.accountMemberships.forEach((membership) => {
          window.analytics.group(
            membership.account.id,
            { name: membership.account.name }
          );
        });
      }

      this.hasIdentifiedUser = true;
    }
  }

  /**
   * @param {string} event
   * @param {object} [data]
   */
  event(event, data = {}) {
    const account_id = this._user?.accountMemberships?.map((membership) => `${membership.account.id}`);
    if (account_id) {
      data = { ...data, account_id };
    }
    if (this.shouldTrackEvents) {
      UserEvent.create({
        event: event.toLowerCase(),
        eventData: data,
      });

      // Pass events to intercom directly from the client to support
      // Intercom's "event driven" behaviors (tours, messages, etc)
      window.analytics.track(
        event,
        data,
        {
          traits: { ...(this._user ?? {}) },
          integrations: this.integrations,
        }
      );
    }
  }

  /**
   * @param {object} props
   */
  page(props) {
    if (this.shouldTrackPageviews) {
      Pageview.create(props);
    }
  }

  /**
   * @param {object} opts
   * @param {string} opts.domain
   * @param {string} opts.protocol
   */
  static configure({ domain = 'data.mysw.com', protocol = 'http' }) {
    Analytics.Pageview.$AR.url = `${protocol}://${domain}/api/activity/v1/pageviews`;
    Analytics.UserEvent.$AR.url = `${protocol}://${domain}/api/activity/v1/user-events`;
  }
}

Analytics.Pageview = Pageview;
Analytics.UserEvent = UserEvent;

/**
 * @private
 * @param {object} context
 * @param {function} fn
 * @param {Promise} hasLoadedPromise
 * @return {function}
 */
function defer(context, fn, hasLoadedPromise) {
  return (...args) => {
    hasLoadedPromise.then(() => fn.call(context, ...args));
  };
}

/**
 * @private
 * @param {string} key
 */
function load(key) {
  // Create a queue, but don't obliterate an existing one!
  const analytics = window.analytics = window.analytics || [];

  // The real analytics.js is already on the page
  if (analytics.initialize || analytics.invoked) {
    return;
  }

  analytics.invoked = true;

  // A list of the methods in Analytics.js to stub.
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'group',
    'track',
    'ready',
    'alias',
    'page',
    'once',
    'off',
    'on',
  ];

  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so we can replay the data.
  analytics.factory = function(method) {
    return function() {
      // eslint-disable-next-line
      const args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };

  // For each of our methods, generate a queueing stub.
  for (let i = 0; i < analytics.methods.length; i += 1) {
    const key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  }

  // Define a method to load Analytics.js from our CDN,
  // and that will be sure to only ever load it once.
  let hasRunLoad = false;
  analytics.load = function(key) {
    if (hasRunLoad) return;
    // Create an async script element based on your key.
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${document.location.protocol === 'https:' ? 'https://' : 'http://'}cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;
    script.onerror = function() {
      window.onSegmentError();
    };

    // Insert our script next to the first script element.
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(script, first);
    hasRunLoad = true;
  };

  analytics.ready(() => window.onSegmentLoad());
  analytics.load(key);

  // Add a version to keep track of what's in the wild.
  analytics.SNIPPET_VERSION = '3.0.1';
}

export default Analytics;
